import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { ConfirmDialog, FormField, Banner, Button } from "../common";
import { deleteEmployee } from "../../services/employeeLifecycleApi";
import { getErrorMessage, getErrorCode } from "../../utils/errors";

// [NEW] Permanently deletes an employee (DELETE /employees/{id}).
// The server only allows it when nothing else refers to the person; otherwise it answers
// RECORD_IN_USE and the dialog explains that deactivating is the right action instead.
//   employee            the row to delete (dialog is open while this is set)
//   onClose()           close without deleting
//   onDeleted(employee) called after a successful delete (refresh your list here)
//   onDeactivate(row)   optional: shows a "Deactivate instead" button when the employee cannot be deleted
export default function DeleteEmployeeDialog({ employee, onClose, onDeleted, onDeactivate }) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [inUse, setInUse] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (employee) { setReason(""); setError(""); setInUse(false); } }, [employee]);

  const confirm = async () => {
    if (!reason.trim()) { setError("Please give a reason. It is saved in the audit trail."); return; }
    setBusy(true);
    try {
      await deleteEmployee(employee.id, reason);
      toast.success(`${employee.name} was deleted.`);
      onDeleted?.(employee);
    } catch (err) {
      const code = getErrorCode(err);
      setInUse(code === "RECORD_IN_USE");
      setError(code === "SELF_DELETE_FORBIDDEN" ? "You cannot delete your own employee record." : getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ConfirmDialog
      open={Boolean(employee)}
      title={`Delete ${employee?.name || "employee"}?`}
      message="This permanently removes the employee record and cannot be undone. It only works for someone with no login, roles, reporting lines, goals, appraisals or history. Otherwise deactivate them, which keeps their history."
      confirmLabel="Delete permanently"
      tone="danger"
      loading={busy}
      onConfirm={confirm}
      onClose={onClose}
    >
      {employee && (
        <div className="form-stack">
          {inUse && (
            <Banner
              tone="warning"
              title={`${employee.name} can't be deleted`}
              action={onDeactivate && <Button size="sm" variant="secondary" onClick={() => onDeactivate(employee)}>Deactivate instead</Button>}
            >
              {error}
            </Banner>
          )}
          <FormField label="Reason" required error={!inUse && error ? error : undefined} hint="Saved in the audit trail.">
            <textarea rows={2} maxLength={1000} value={reason} onChange={(e) => { setReason(e.target.value); setError(""); setInUse(false); }} />
          </FormField>
        </div>
      )}
    </ConfirmDialog>
  );
}
